import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/Toast';
import ProtectedRoute from './components/ProtectedRoute';
import { SupplierLocale } from './pages/supplier/SupplierLocale';
import ScrollToTop from './components/ScrollToTop';
import Login from './pages/Login';
import StaffDashboard from './pages/staff/StaffDashboard';
import SpainDashboard from './pages/es/SpainDashboard';
import PadelanteDashboard from './pages/pad/PadelanteDashboard';
import CustomerDashboard from './pages/customer/CustomerDashboard';
import NewRequest from './pages/customer/NewRequest';
import CustomerRequestDetail from './pages/customer/RequestDetail';
import SupplierDashboard from './pages/supplier/SupplierDashboard';
import SupplierTourDetail from './pages/supplier/SupplierTourDetail';
import ResellerDashboard from './pages/reseller/ResellerDashboard';
import MobileScan from './pages/MobileScan';
import ConfirmItinerary from './pages/ConfirmItinerary';
import SkeletalApprovalPage from './pages/SkeletalApprovalPage';
import SkeletalInternalApprovalPage from './pages/SkeletalInternalApprovalPage';
import QuoteApprovalPage from './pages/QuoteApprovalPage';
import SignContract from './pages/SignContract';
import TeamInfoForm from './pages/TeamInfoForm';
import ParticipantRegister from './pages/ParticipantRegister';
import RoomingAssignmentPage from './pages/RoomingAssignmentPage';
import TeamsAssignmentPage from './pages/TeamsAssignmentPage';
import TourLeaderDashboard from './pages/tour-leader/TourLeaderDashboard';
import FootballOpsDashboard from './pages/football-ops/FootballOpsDashboard';
import TeamDatabase from './pages/TeamDatabase';
import TeamDatabaseRespond from './pages/TeamDatabaseRespond';
import SurveyRespond from './pages/SurveyRespond';
import MarketingDashboard from './pages/marketing/MarketingDashboard';
import JoinMST from './pages/JoinMST';
import PadelanteLead from './pages/PadelanteLead';
import Quote from './pages/Quote';

function App() {
  return (
    <ToastProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* UK → Spain portal + the combined "All Tours" roll-up. Gated on the
              uk_to_spain portal, but any multi-portal user may enter for "All". */}
          <Route path="/staff/*" element={
            <ProtectedRoute requirePortal="uk_to_spain" allowMultiPortal>
              <StaffDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/*" element={
            <ProtectedRoute requirePortal="uk_to_spain" allowMultiPortal>
              <StaffDashboard />
            </ProtectedRoute>
          } />
          {/* Spanish staff portal — Spain → UK tours. */}
          <Route path="/es/*" element={
            <ProtectedRoute requirePortal="spain_to_uk">
              <SpainDashboard />
            </ProtectedRoute>
          } />
          {/* Padelante padel portal — brand='padelante' tours. */}
          <Route path="/pad/*" element={
            <ProtectedRoute requirePortal="padelante">
              <PadelanteDashboard />
            </ProtectedRoute>
          } />
          <Route path="/customer" element={
            <ProtectedRoute allowedRole="customer">
              <CustomerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/customer/request" element={
            <ProtectedRoute allowedRole="customer">
              <NewRequest />
            </ProtectedRoute>
          } />
          <Route path="/customer/requests/:id" element={
            <ProtectedRoute allowedRole="customer">
              <CustomerRequestDetail />
            </ProtectedRoute>
          } />
          <Route path="/supplier" element={
            <ProtectedRoute allowedRole="supplier">
              <SupplierLocale><SupplierDashboard /></SupplierLocale>
            </ProtectedRoute>
          } />
          <Route path="/supplier/tour/:requestId" element={
            <ProtectedRoute allowedRole="supplier">
              <SupplierLocale><SupplierTourDetail /></SupplierLocale>
            </ProtectedRoute>
          } />
          <Route path="/reseller/*" element={
            <ProtectedRoute allowedRole="reseller">
              <ResellerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/marketing" element={
            <ProtectedRoute allowedRole="marketing">
              <MarketingDashboard />
            </ProtectedRoute>
          } />
          {/* Public route — phone camera upload for QR receipt scanning */}
          <Route path="/scan/:token" element={<MobileScan />} />
          <Route path="/tour-leader/*" element={
            <ProtectedRoute allowedRole="tour_leader">
              <TourLeaderDashboard />
            </ProtectedRoute>
          } />
          {/* Football operations — read-only football side of every tour in
              the user's portals. Admins may look in too. */}
          <Route path="/football-ops/*" element={
            <ProtectedRoute allowedRole={['football_ops', 'admin']}>
              <FootballOpsDashboard />
            </ProtectedRoute>
          } />
          {/* Public route — supplier itinerary confirmation */}
          <Route path="/confirm-itinerary" element={<SupplierLocale><ConfirmItinerary /></SupplierLocale>} />
          {/* Public route — coach skeletal itinerary approval */}
          <Route path="/skeletal-approval" element={<SkeletalApprovalPage />} />
          {/* Public route — internal (MST) skeletal itinerary approval */}
          <Route path="/skeletal-internal-approval" element={<SkeletalInternalApprovalPage />} />
          <Route path="/quote-approval" element={<QuoteApprovalPage />} />
          {/* No-login e-signing of the Sports Tour Contract (contracts/{token}). */}
          <Route path="/sign-contract" element={<SignContract />} />
          {/* Public routes — coach team info + participant registration */}
          <Route path="/team-info" element={<TeamInfoForm />} />
          <Route path="/register" element={<ParticipantRegister />} />
          {/* Public route — room allocation via token link */}
          <Route path="/rooming-assignment" element={<RoomingAssignmentPage />} />
          <Route path="/teams-assignment" element={<TeamsAssignmentPage />} />
          {/* Public routes — team database registration + fixture response */}
          <Route path="/teamdatabase" element={<TeamDatabase />} />
          <Route path="/fixture-respond" element={<TeamDatabaseRespond />} />
          {/* Public route — tour organiser post-tour survey via token link */}
          <Route path="/survey-respond" element={<SurveyRespond />} />
          {/* Public route — unlisted franchise application page */}
          <Route path="/joinmst" element={<JoinMST />} />
          {/* Public route — Padelante lead-capture form (QR codes) */}
          <Route path="/padelante" element={<PadelanteLead />} />
          {/* Public route — instant tour quote builder (internal, MST-branded) */}
          <Route path="/quote" element={<Quote />} />
          {/* Public route — same quote builder, Your Tour-branded, no pricing shown */}
          <Route path="/ytquote" element={<Quote />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ToastProvider>
  );
}

export default App;
